// The Bazaar Android app (android/) — the JS side of its native bridge.
//
// The app is a WebView over this site. Its MainActivity injects ONE object,
// `window.ZohaAndroid`, before the page loads; that global is the host signal
// (hostKey() → "bazaar"), exactly like EitaaWebviewProxy is for Eitaa — no script
// of ours defines it, so it can't exist in a messenger or a browser.
//
// Contract (android/app/src/main/java/com/peyvand/zoha/Bridge.kt):
//   ZohaAndroid.call(method, argsJson, callbackId)          JS → native, async
//   window.__zohaNative(callbackId, ok, resultJson)          native → JS reply
//   window.__zohaBack() → "true" | "false"                  native asks on the hardware back key
//
// Methods: signIn · lastSignedIn · storageGet · storageSave · purchase · unconsumed
//          · consume · openLink · download · close · appVersion
//
// androidWebApp() dresses this up as the Telegram-shaped `WebApp` the rest of the
// Mini App already speaks (BackButton, openLink, downloadFile, close), so
// backbutton.js / host.js / openExternal() work in the app with no Android branch.

export function isAndroidApp() {
  try { return typeof window.ZohaAndroid !== "undefined"; } catch { return false; }
}

const pending = new Map();
let nextId = 1;

if (typeof window !== "undefined") {
  window.__zohaNative = (id, ok, json) => {
    const p = pending.get(String(id));
    if (!p) return;
    pending.delete(String(id));
    let data = null;
    try { data = json ? JSON.parse(json) : null; } catch { data = json; }
    ok ? p.resolve(data) : p.reject(Object.assign(new Error(data?.message || "native error"), data || {}));
  };
}

export function nativeCall(method, args = {}) {
  return new Promise((resolve, reject) => {
    if (!isAndroidApp()) { reject(new Error("not in the Android app")); return; }
    const id = String(nextId++);
    pending.set(id, { resolve, reject });
    try { window.ZohaAndroid.call(method, JSON.stringify(args || {}), id); }
    catch (e) { pending.delete(id); reject(e); }
  });
}

// ── «ورود با بازار» ───────────────────────────────────────────────────────────
// Bazaar gives only an account id, so the server pairs it with a secret the app
// keeps in BazaarStorage (Bazaar's own per-account storage) — routers/auth.py.

async function exchange(accountId, apiFetch) {
  let secret = null;
  try { secret = (await nativeCall("storageGet"))?.data || null; } catch (_) {}
  const data = await apiFetch("/auth/bazaar", {
    method: "POST",
    body: JSON.stringify({ account_id: accountId, secret, lang: localStorage.getItem("zoha_lang") || undefined }),
  });
  if (data?.device_secret) {
    // Bind only once Bazaar confirms it kept the secret — otherwise a parent whose
    // Bazaar can't store data would be locked out of their own account later.
    nativeCall("storageSave", { data: data.device_secret })
      .then(r => r?.ok && apiFetch("/auth/bazaar/bind", {
        method: "POST", body: JSON.stringify({ secret: data.device_secret }),
        headers: { Authorization: `Bearer ${data.access_token}` },
      }))
      .catch(() => {});
  }
  return data;
}

// Silent: the account the parent already granted, or null (never shows a page).
export async function bazaarSilentSignIn(apiFetch) {
  let acc = null;
  try { acc = await nativeCall("lastSignedIn"); } catch (_) { return null; }
  if (!acc?.accountId) return null;
  return exchange(acc.accountId, apiFetch);
}

// Interactive: Bazaar's own sign-in page. Resolves null when the parent declines.
export async function bazaarSignIn(apiFetch) {
  const acc = await nativeCall("signIn");
  if (!acc?.accountId) return null;
  return exchange(acc.accountId, apiFetch);
}

// ── Purchases ─────────────────────────────────────────────────────────────────

// Opens Bazaar's purchase sheet. Resolves {purchaseToken, productId, payload,
// originalJson, dataSignature} (the last two: Bazaar's signed copy, app builds ≥ 2);
// rejects with {cancelled: true} when the parent backs out.
export function bazaarPurchase({ sku, payload, dynamic_price_token }) {
  return nativeCall("purchase", { sku, payload, dynamicPriceToken: dynamic_price_token || "" });
}

// Purchases Bazaar still holds as unconsumed — a payment whose verify never reached
// the server (the app died, the network dropped). Re-sent on every launch.
export async function unconsumedPurchases() {
  try { return (await nativeCall("unconsumed")) || []; } catch { return []; }
}

// ── The WebApp-shaped bridge (for webApp()) ───────────────────────────────────

let backVisible = false;
let backHandlers = [];
if (typeof window !== "undefined") {
  // Native asks this on the hardware back key: "true" = the page handled it.
  window.__zohaBack = () => {
    if (!backVisible || !backHandlers.length) return "false";
    backHandlers.forEach(fn => { try { fn(); } catch (_) {} });
    return "true";
  };
}

let shim = null;
export function androidWebApp() {
  if (!isAndroidApp()) return null;
  if (shim) return shim;
  shim = {
    // Above every version gate in host.js, so capability probes decide alone.
    version: "8.0",
    platform: "android",
    initData: "",
    initDataUnsafe: {},
    ready() {}, expand() {},
    close() { nativeCall("close").catch(() => {}); },
    openLink(url) { nativeCall("openLink", { url }).catch(() => {}); },
    downloadFile({ url, file_name }) { nativeCall("download", { url, fileName: file_name }).catch(() => {}); },
    BackButton: {
      get isVisible() { return backVisible; },
      show() { backVisible = true; },
      hide() { backVisible = false; },
      onClick(fn) { if (typeof fn === "function") backHandlers.push(fn); },
      offClick(fn) { backHandlers = backHandlers.filter(h => h !== fn); },
    },
  };
  return shim;
}
